import { useEffect, useRef, useState } from 'react';
import { useScroll } from 'react-use';

import {
  useCart,
  CartLineProvider,
  Money,
} from '@shopify/hydrogen';

import { CartLineItem, CartEmpty } from '~/rs-components/cart';
import { Text } from '~/rs-components/elements'
import { getCurrencyValue } from '~/lib/utils';
import { digitalagent, showAcceptTermsAndConditions, showDropDownCustomerAccounts } from '../../../setup/settings/__product';
import { continueToCheckoutButtonName, continueToShoppingButtonName, deliveryExtraInformation, pickeasy, retailSystem, storeCredentials, useShopifyCheckoutURL } from '../../../setup/settings';
import { CustomerAccounts } from './CustomerAccounts.client';
import { CustomerAccount } from '~/types';
import axios from 'axios';
import Pickeasy, { callCartUpdateAtributte, callCartUpdateNote } from '../../../setup/components/Pickeasy.client';
import CustomDeliveryInformationCart from '../../../setup/components/CustomDeliveryInformationCart.client';
// import { CustomersList } from '~/rs-components/cart';

type BodyProp = {
  accountId: string
  customerId: string,
  cartToken: string,
  shopifyStoreUrl: string,
  name: string
}


export function CartDetails({
  layout,
  onClose,
  currency,
  customerAccounts
}: {
  layout: 'drawer' | 'page';
  onClose?: () => void;
  currency: string;
  customerAccounts?: CustomerAccount[]
}) {
  const { lines, id } = useCart();
  const scrollRef = useRef(null);
  const { y } = useScroll(scrollRef);
  const [checkoutConfirm, setCheckoutConfirm] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [payloadPickyEasy, setPayloadPickyEasy] = useState<any>(null);

  const defaultBody: BodyProp = {
    accountId: customerAccounts && customerAccounts.length > 0 && customerAccounts[0].accountId !== undefined ? customerAccounts[0].accountId : "",
    customerId: customerAccounts && customerAccounts.length > 0 && customerAccounts[0].id !== undefined ? customerAccounts[0]?.id : "",
    cartToken: "",
    shopifyStoreUrl: storeCredentials.storeDomain ?? "",
    name: customerAccounts && customerAccounts.length > 0 && customerAccounts[0].name !== undefined ? customerAccounts[0]?.name : ""
  }
  const [body, setBody] = useState<BodyProp>(defaultBody);


  if (lines.length === 0 && !orderSent) {
    return <CartEmpty onClose={onClose} />;
  }

  if (orderSent) {
    return (
      <div
        ref={scrollRef}
        className={`grid content-start gap-4 px-6 pb-8 transition overflow-y-scroll md:gap-12 md:px-12 h-screen-no-nav md:pb-12 ${y > 0 && 'border-t'
          }`}>
        <section className="grid gap-6 mt-16">
          <Text size='lead' format>
            Your order has been processed, thank you.
          </Text>
          <div>
            <button className='black-button' onClick={onClose}>Continue Browsing</button>
          </div>
        </section>
      </div>
    )
  }

  const container = {
    drawer: 'grid grid-cols-1 h-screen-no-nav grid-rows-[1fr_auto]',
    page: 'pb-12 max-w-7xl mx-auto w-full flex flex-col md:flex-row md:items-start gap-4 md:gap-8 lg:gap-12',
  };

  const content = {
    drawer: 'px-6 pb-6 sm-max:pt-2 overflow-auto transition md:px-12',
    page: 'flex-grow md:translate-y-4',
  };

  const summary = {
    drawer: 'grid gap-6 p-6 border-t md:px-12',
    page: 'sticky top-nav grid gap-6 p-4 md:px-6 md:translate-y-4 bg-primary/5 rounded w-full max-w-md',
  };

  const onSelectedCustomer = (customerId: string) => {
    if (customerAccounts) {
      const selectedCustomer = customerAccounts.find(customer => customer.id === customerId);

      if (selectedCustomer) {
        const { accountId = "", id = "", name = "" } = selectedCustomer;
        const updateBody: BodyProp = {
          ...body,
          accountId,
          customerId: id,
          name
        };
        setBody(updateBody);
      }

    }
  }
  const onSelectPicky = (payload: any) => {
    setPayloadPickyEasy(payload)
  }
  return (
    <div className="cartContainerMenu">
      <form className={container[layout]}>
        <section
          ref={scrollRef}
          aria-labelledby="cart-contents"
          className={`${content[layout]} ${y > 0 && 'border-t'} section-content-line`}
        >
          <ul className="grid gap-6 md:gap-10">
            {lines.map((line) => {
              return (
                <CartLineProvider key={line.id} line={line}>
                  <CartLineItem currency={currency} />
                </CartLineProvider>
              );
            })}
          </ul>
        </section>
        <section aria-labelledby="summary-heading" className={`${summary[layout]} ${pickeasy.enabled && 'overflowPickeasy'}`}>
          <h2 id="summary-heading" className="sr-only">
            Order summary
          </h2>
          <OrderSummary currency={currency} />
          {showDropDownCustomerAccounts.visible && customerAccounts && !checkoutConfirm &&
            <CustomerAccounts onSelectCustomer={onSelectedCustomer} customerAccounts={customerAccounts} />
          }
          {digitalagent && !checkoutConfirm && <span className='black-button back' onClick={onClose}>Continue Adding Products</span>}
          {pickeasy.enabled && <Pickeasy onSelectPicky={onSelectPicky} />}
          <CartCheckoutActions payloadPickyEasy={payloadPickyEasy} body={body} checkoutConfirm={checkoutConfirm} setCheckoutConfirm={setCheckoutConfirm} setOrderSent={setOrderSent} onClose={onClose} />
        </section>
      </form>
    </div >
  );
}

function CartCheckoutActions({ body, checkoutConfirm, setCheckoutConfirm, setOrderSent, onClose, payloadPickyEasy }: { body: BodyProp }) {
  const { checkoutUrl, id, linesRemove, lines } = useCart();
  const [isChecked, setIsChecked] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const newCheckoutUrl = useShopifyCheckoutURL?.active ? checkoutUrl?.replace(useShopifyCheckoutURL.url, storeCredentials.storeDomain) : checkoutUrl;
  const [failResponse, setFailResponse] = useState(false); // Array instead of object
  const [sendingOrder, setSendingOrder] = useState(false);

  const [pickyEasyInfo, setPickyEasyInfo] = useState(payloadPickyEasy ?? null);
  const [failResponsePickyEasy, setFailResponsePickyEasy] = useState<string>("");

  useEffect(() => {
    setPickyEasyInfo(payloadPickyEasy ?? null);
    setFailResponsePickyEasy("")
  }, [payloadPickyEasy]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFailResponsePickyEasy("");
    }, 3000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [failResponsePickyEasy]);


  const handleClick = () => {
    if (!isChecked) {
      setIsButtonClicked(true);
    }
  }

  const continueToCheckout = async () => {
    if (showDropDownCustomerAccounts.visible) {
      await processCustomerAccounts(checkoutConfirm, setCheckoutConfirm, setFailResponse, id, body, setSendingOrder, sendOrder);
    } else {
      if (pickeasy.enabled && pickyEasyInfo) {
        await processPickeasy(pickyEasyInfo, newCheckoutUrl, setPickyEasyInfo, setFailResponsePickyEasy, id);

      } else {
        //default
        window.location.replace(newCheckoutUrl);
      }
    }
  }
  async function getOptions() {
    let tokenKey = id?.split('/') ? id?.split('/')[id?.split('/').length - 1] : "";
    const cartToken = tokenKey.split('?')[0];

    try {
      const response = await axios.get(`${retailSystem.cartCache}/GetCartVariantOptions/${cartToken}`);
      return response.data.cartLineItems;

    } catch (error) {
      console.error('Error en la solicitud GET:', error);
    }
  }
  const sendOrder = async () => {

    const attributes = await getOptions();
    const bodyJson = {
      StoreId: storeCredentials.storeId,
      Customer: {
        Id: body.customerId,
        Name: body.name,
        Email: "dummy@email.com",
        Phone: "1234567890"
      },
      DestinationAccountId: body.accountId,
      LineItems: lines.map(l => {
        return {
          ShopifyProductId: l.merchandise.product.id.split('/')[l.merchandise.product.id.split('/').length - 1],
          ProductName: l.merchandise.product.title,
          Price: +(l.cost.totalAmount.amount ?? 0) / l.quantity,
          Quantity: l.quantity,
          OptionsIds: attributes?.find((c: any) => c.lineItemId == l.merchandise.id?.split('/')[l.merchandise.id?.split('/').length - 1])?.options.map((op: any) => (op?.option?.Id))
        }
      })
    }

    fetch(retailSystem?.createOrder ?? '', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'eyJhbGciOiJIUzI1NiJ9eyJSb2xlIjoiQWRtaW4iLCJJc3N1ZXIiOiJJc3N1ZXIiLCJVc2Vybm'
      },
      body: JSON.stringify(bodyJson),
    }).then(res => {
      if (res.ok) {
        setFailResponse(false)
        linesRemove(lines.map(l => l.id))
        setOrderSent(true);
      } else {
        setFailResponse(true)
      }
    }).catch(error => {
      setFailResponse(true)
      console.error(error.toString())
    }).finally(_ => {
      setSendingOrder(false);
    })
  }

  if (checkoutConfirm && !failResponse) {
    return (
      <div className="buttonCart grid gap-4">
        {sendingOrder ? <p className='font-bold mt-6 mb-6'>Sending Order...</p> : <>
          <span onClick={() => setCheckoutConfirm(false)} className="black-button back ">Back</span>
          <span onClick={continueToCheckout} className="black-button">PLACE ORDER</span>
        </>
        }
      </div>
    )
  }
  else return (
    <>

      {showAcceptTermsAndConditions.visible ?
        <div>
          <div className={`checkboxTerms ${isButtonClicked && !isChecked ? 'highlight' : ''}`}>
            <input type="checkbox" id="CheckboxTerms" name="CheckboxTerms" value="isChecked" onChange={() => setIsChecked(!isChecked)} />
            <label htmlFor="CheckboxTerms" className="labelCheckBox">I agree to the <a className="linkTerms" href={`${showAcceptTermsAndConditions.url}`}>terms and conditions</a></label>
          </div>
          <div className="buttonCart grid gap-4">
            {isChecked ?
              <span onClick={continueToCheckout} className="black-button">{continueToCheckoutButtonName}</span>
              :
              <span className={`black-button ${!isChecked ? 'disabled' : ''}`} onClick={handleClick}>{continueToCheckoutButtonName}</span>
            }
          </div>
        </div> :

        <>
          {continueToShoppingButtonName.enabled && <span className='black-button continue' onClick={onClose}>{continueToShoppingButtonName.text}</span>}

          {pickeasy.enabled ?
            <>
              <span onClick={pickyEasyInfo != null ? continueToCheckout : undefined} className={`black-button ${(pickyEasyInfo == null) ? ' disabled' : ''}`}>
                {pickyEasyInfo != null ? continueToCheckoutButtonName : pickeasy.textButtonCart}
              </span>
              {failResponsePickyEasy != "" && <p className="title-error" >{failResponsePickyEasy}</p>}
            </>

            :

            <div className="buttonCart grid gap-4">
              <span onClick={continueToCheckout} className="black-button">{continueToCheckoutButtonName}</span>
            </div>
          }

        </>
      }
      {showDropDownCustomerAccounts.visible && failResponse && <p className="title-error" >Something went wrong...</p>}
    </>
  );
}

async function processPickeasy(pickyEasy: any, newCheckoutUrl: any, setPickyEasy, setFailResponsePickyEasy, id) {
  try {

    //const response = await callCartUpdateAtributte(payloadAttribute);

    const response = await callCartUpdateNote(pickyEasy);

    if (response && response.status === 200) {
      console.log("response", response)
      window.location.replace(newCheckoutUrl);
    } else {
      setPickyEasy(null);
      setFailResponsePickyEasy("An error occurred, please try again later");
      console.error(`An error occurred, callCartUpdateNote. Status: ${response.status}`);

    }
  } catch (error) {
    setPickyEasy(null);
    setFailResponsePickyEasy("An error occurred, please try again later");
    console.error("An error occurred, callCartUpdateNote:", error);

  }
}

async function processCustomerAccounts(checkoutConfirm: any, setCheckoutConfirm: any, setFailResponse, id: any, body: BodyProp, setSendingOrder, sendOrder: () => Promise<void>) {
  if (!checkoutConfirm) {
    setCheckoutConfirm(true);
  }
  else {
    setFailResponse(false);
    let tokenKey = id?.split('/') ? id?.split('/')[id?.split('/').length - 1] : "";
    const cartToken = tokenKey.split('?')[0];
    body.cartToken = cartToken ?? "";
    // await saveCartInfo(body)
    //todo: send order to retail system
    setSendingOrder(true);
    await sendOrder();
  }
}

function OrderSummary({ currency }: { currency: string }) {
  const { cost } = useCart();
  return (
    <>
      <dl className="grid">
        <div className="flex items-center justify-between font-medium">
          <Text as="dt">Subtotal</Text>
          <Text as="dd">
            {cost?.subtotalAmount?.amount ? (
              // <Money data={cost?.subtotalAmount} />
              getCurrencyValue(currency, cost?.subtotalAmount.amount)
            ) : (
              '-'
            )}
          </Text>
        </div>
        {deliveryExtraInformation && (
          <CustomDeliveryInformationCart subtotal={cost?.subtotalAmount.amount} />
        )}
      </dl>
    </>
  );
}
